import React, { useEffect, useState, useCallback } from "react";
import * as Icons from "lucide-react";
import { Lock, Clock } from "lucide-react";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";
import { api, errMsg, naira } from "@/lib/api";

function fmt(sec) {
  const m = Math.floor(sec / 60);
  const s = sec % 60;
  return m > 0 ? `${m}m ${s}s` : `${s}s`;
}

export default function Jobs() {
  const { setCharacter } = useAuth();
  const [jobs, setJobs] = useState([]);
  const [level, setLevel] = useState(1);
  const [busy, setBusy] = useState(null);

  const load = useCallback(async () => {
    try {
      const { data } = await api.get("/jobs");
      setJobs(data.jobs);
      setLevel(data.level);
    } catch (e) { toast.error(errMsg(e)); }
  }, []);

  useEffect(() => {
    load();
    const t = setInterval(() => {
      setJobs((js) => js.map((j) => ({ ...j, remaining: Math.max(0, j.remaining - 1) })));
    }, 1000);
    return () => clearInterval(t);
  }, [load]);

  const work = async (id) => {
    setBusy(id);
    try {
      const { data } = await api.post("/jobs/work", { job_id: id });
      setCharacter(data.character);
      toast.success(`Shift done! +${naira(data.earned)} · +${data.xp}xp`);
      await load();
    } catch (e) { toast.error(errMsg(e)); }
    finally { setBusy(null); }
  };

  return (
    <div className="px-5 pt-6" data-testid="jobs-page">
      <span className="label-xs text-slate-500">In-game Phone · Jobs</span>
      <h1 className="font-display text-3xl mt-1">Pick Your <span className="text-[#16A34A]">Hustle</span></h1>
      <p className="text-sm text-slate-500 mt-1">Level {level}. Each shift burns energy & has a cooldown. No shortcut.</p>

      <div className="mt-6 space-y-3 mb-4">
        {jobs.map((j) => {
          const Icon = Icons[j.icon.split("-").map((s) => s[0].toUpperCase() + s.slice(1)).join("")] || Icons.Briefcase;
          const onCd = j.remaining > 0;
          const locked = j.locked;
          return (
            <div key={j.id} data-testid={`job-${j.id}`}
              className={`bg-white border rounded-xl p-4 ${locked ? "border-slate-200 opacity-60" : "border-slate-200"}`}>
              <div className="flex items-start gap-3">
                <div className="w-11 h-11 rounded-xl bg-black/60 flex items-center justify-center shrink-0">
                  <Icon size={20} className="text-[#16A34A]" />
                </div>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center justify-between">
                    <span className="font-semibold text-slate-900">{j.title}</span>
                    <span className="text-[#16A34A] font-display">{naira(j.pay)}</span>
                  </div>
                  <p className="text-xs text-slate-500 mt-0.5 leading-tight">{j.desc}</p>
                  <div className="flex items-center gap-3 mt-2 label-xs text-slate-400">
                    <span>⚡ -{j.energy_cost}</span>
                    <span>+{j.xp}xp</span>
                    {j.clout > 0 && <span className="text-[#FF0055]">+{j.clout} clout</span>}
                  </div>
                </div>
              </div>
              <button
                data-testid={`work-${j.id}`}
                disabled={locked || onCd || busy === j.id}
                onClick={() => work(j.id)}
                className={`mt-3 w-full py-2.5 rounded-lg label-xs flex items-center justify-center gap-2 transition-colors ${
                  locked ? "bg-black/40 text-slate-400"
                  : onCd ? "bg-black/40 text-slate-500"
                  : "bg-[#16A34A] text-white hover:bg-[#4dff94]"
                }`}
              >
                {locked ? <><Lock size={13} /> Unlocks at Lv{j.min_level}</>
                  : onCd ? <><Clock size={13} /> {fmt(j.remaining)}</>
                  : busy === j.id ? "Working…" : "Work Shift"}
              </button>
            </div>
          );
        })}
      </div>
    </div>
  );
}
